// Origem do lead (VH) — puro. ESPELHO de `functions/messaging/origens.js` para a
// lista padrão e a inferência por palavra solta; o que só existe aqui são as
// operações da aba "Origens do lead" e `origemPorId`.
//
// ★ A origem é INFERIDA na criação por mensagem (referral > código/palavra da
// regra da Captação > palavra solta daqui), atrás da chave `config.inferirOrigem`,
// com `origemInferida: true` no lead; a SDR troca no select e a marca cai — a
// inferência nunca sobrescreve escolha humana, e nunca toca lead existente.
// ★ REMOVIDA = `ativo: false`: some do select, continua legível em quem já tem.
// ★ A aba semeia da MESMA fonte que o runtime lê (`config.origens ?? PADRÃO`).
import { normalizar, palavraCasa } from '@/leads/inferencia';
import { ORIGENS_LEGADAS } from '@/lib/constants';

const o = (id, label, palavras = []) => ({ id, label, ativo: true, palavras });

/** Lista padrão do dono, NESTA ordem — igual à do servidor (teste de espelho). */
export const ORIGENS_PADRAO = [
  o('google_business', 'Google Business', ['google', 'perfil da empresa']),
  o('meta_ads', 'Meta Ads', ['redes sociais', 'anuncio', 'instagram', 'facebook', 'vim pelo anuncio']),
  o('site_google', 'Site Google', ['site', 'pelo site']),
  o('indicacao', 'Indicação', ['indicacao', 'indicada', 'indicado', 'indicou']),
  o('fachada', 'Fachada', []),
  o('comentarios_seguidores', 'Comentários/Novos seguidores', ['comentario', 'seguindo', 'te sigo']),
  o('paciente_antigo', 'Paciente antigo', []),
];

const valida = (x) => x && typeof x.id === 'string' && x.id.length > 0;

export function sanearOrigens(lista) {
  const vistos = new Set();
  const saida = [];
  for (const x of Array.isArray(lista) ? lista : []) {
    if (!valida(x) || vistos.has(x.id)) continue;
    vistos.add(x.id);
    saida.push({
      id: x.id,
      label: String(x.label ?? x.id).trim() || x.id,
      ativo: x.ativo !== false,
      palavras: (Array.isArray(x.palavras) ? x.palavras : []).map((p) => String(p ?? '').trim()).filter(Boolean),
    });
  }
  return saida;
}

/** `config.origens` saneada, senão o padrão — a MESMA fonte para a aba e para a ficha. */
export function listaDeOrigens(config) {
  const daConfig = config?.origens;
  return Array.isArray(daConfig) ? sanearOrigens(daConfig) : ORIGENS_PADRAO;
}

/** Só as que podem ser escolhidas para um lead (Novo lead, ficha, regra da Captação). */
export const origensAtivas = (config) => listaDeOrigens(config).filter((x) => x.ativo);

export const chaveLigada = (config) => config?.inferirOrigem === true;

/** Valor do FILTRO "Sem origem" (nunca é valor de lead): casa vazio, ausente e legado. */
export const SEM_ORIGEM = '__sem_origem__';

/**
 * ★ VH-1 — UMA função para "este lead não tem origem": vazio, ausente ou um id
 * LEGADO (`ORIGENS_LEGADAS`). O documento continua com o id antigo gravado; é a
 * leitura que o trata como vazio, até a SDR escolher.
 */
export const ehSemOrigem = (id) => !id || ORIGENS_LEGADAS.includes(id);

/**
 * ★ O rótulo de uma origem, NUM LUGAR SÓ: lista da clínica (inclusive inativa,
 * para quem já tem) → (legado | vazio | ausente) "—" → id desconhecido cru.
 */
export function origemPorId(config, id) {
  const viva = id ? listaDeOrigens(config).find((x) => x.id === id) : null;
  if (viva) return { ...viva, vazia: false };
  if (ehSemOrigem(id)) return { id: '', label: '—', ativo: false, vazia: true };
  return { id, label: id, ativo: false, vazia: false };
}

/** Espelho do servidor: palavra solta, ordem da lista, só ativas, vazio → null. */
export function inferirOrigem(texto, origens = ORIGENS_PADRAO) {
  const t = normalizar(texto);
  if (!t) return null;
  for (const origem of sanearOrigens(origens).filter((x) => x.ativo)) {
    if (origem.palavras.some((p) => palavraCasa(p, t))) return { id: origem.id };
  }
  return null;
}

// ---- operações da aba "Origens do lead" (puras: devolvem a lista nova) ----

/** id a partir do rótulo; nunca um id legado nem um já usado (sufixo numérico). */
export function idDeOrigem(label, existentes = []) {
  const base =
    String(label ?? '')
      .normalize('NFD')
      .replace(/\p{M}+/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'origem';
  const usados = new Set([...(existentes ?? []).map((x) => x.id), ...ORIGENS_LEGADAS]);
  if (!usados.has(base)) return base;
  let n = 2;
  while (usados.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

/** "a, b , c" → ['a','b','c']; já aceita lista. */
export const palavrasDeTexto = (texto) =>
  (Array.isArray(texto) ? texto : String(texto ?? '').split(','))
    .map((p) => String(p ?? '').trim())
    .filter(Boolean);

export function adicionarOrigem(lista, label, palavras = []) {
  const atual = sanearOrigens(lista);
  const limpo = String(label ?? '').trim();
  if (!limpo) return atual;
  return [...atual, { id: idDeOrigem(limpo, atual), label: limpo, ativo: true, palavras: palavrasDeTexto(palavras) }];
}

/** "Remover" nunca apaga: marca inativa. Reativar é o inverso. */
export const inativarOrigem = (lista, id) =>
  sanearOrigens(lista).map((x) => (x.id === id ? { ...x, ativo: false } : x));

export const reativarOrigem = (lista, id) =>
  sanearOrigens(lista).map((x) => (x.id === id ? { ...x, ativo: true } : x));

export const renomearOrigem = (lista, id, label) =>
  sanearOrigens(lista).map((x) => (x.id === id ? { ...x, label: String(label ?? '').trim() || x.label } : x));

export const definirPalavras = (lista, id, palavras) =>
  sanearOrigens(lista).map((x) => (x.id === id ? { ...x, palavras: palavrasDeTexto(palavras) } : x));

/** Sobe (delta -1) ou desce (delta +1) — a ordem é a prioridade da palavra solta e a do select. */
export function moverOrigem(lista, id, delta) {
  const atual = sanearOrigens(lista);
  const i = atual.findIndex((x) => x.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= atual.length) return atual;
  const copia = [...atual];
  [copia[i], copia[j]] = [copia[j], copia[i]];
  return copia;
}
